import React from "react";
import {
  AppState,
  DrugCosts,
  PlanType,
  UseTranslateType,
} from "../../../@types";
import { isOverThirtyDaysSinceLogin, isUrlPlanIdParam } from "../../../helpers";
import { hasMailOrderPharmacy } from "../../store/AppStore";
import { alertMessageHierarchy } from "./constants";
import { Routes } from "../..";
import { FormattedMessage } from "react-intl";
import {
  AlertMessageConditions,
  AlertMessageHierarchyKeys,
  AlertMessageType,
  FormattedAlertMessage,
  MessagesState,
  PageContext,
} from "./types";
import { AlertMessageLink } from "../../../components";

export function flattenMessagingState(
  state: Record<AlertMessageConditions, AlertMessageType>
): AlertMessageType[] {
  const result: AlertMessageType[] = [];
  for (const [key, value] of Object.entries(state)) {
    result[key] = value;
  }
  return result;
}

const formatMessage = ({
  selectedMessage,
  t,
  pageContext,
}: {
  selectedMessage: AlertMessageType;
  t: UseTranslateType;
  pageContext?: PageContext;
}): FormattedAlertMessage => ({
  ...selectedMessage,
  title: selectedMessage.title ? selectedMessage.title(t) : undefined,
  action: selectedMessage.action
    ? selectedMessage.action(t, pageContext)
    : undefined,
  body: selectedMessage.body(t, pageContext),
});

export function getMessageToShow(
  availableMessages: AlertMessageType[],
  messageOrderHierarchy: Partial<AlertMessageConditions>[],
  t: UseTranslateType,
  pageContext: PageContext = {}
): FormattedAlertMessage | undefined {
  const { planType, planId, planHasPreferredPharmacies } = pageContext;
  let result = undefined;
  for (let i = 0; i < messageOrderHierarchy.length; i++) {
    const messageKey = messageOrderHierarchy[i];
    const selectedMessage = availableMessages[messageKey];
    if (selectedMessage.active) {
      if (
        selectedMessage?.key ===
          AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES &&
        planId &&
        !planHasPreferredPharmacies
      ) {
        // supress preferred pharmacy alert if the plan does not offer any preferred pharmacies
        continue;
      } else if (
        planType === PlanType.MA &&
        selectedMessage?.key !== AlertMessageConditions.NO_SAVED_DRUGS
      ) {
        continue;
      } else {
        result = formatMessage({ selectedMessage, t, pageContext });
        break;
      }
    }
  }
  return result;
}

/**
 * Overrides default LCP Alert Messaging by examining DrugCosts to derive Network
 * Status messages. It is used for Plan Compare (multiple plans on a page)
 *
 * - If no pharmacy is in-network, returns a message prompting to find an in-network
 * pharmacy
 * - If any pharmacy is in-network but there are no preferred pharmacies, returns
 * a message prompting to find a preferred pharmacy
 * - If any other type of message is set to `active`, returns `undefined`
 *
 * This helper does not rely on the `active` property of network-status messages,
 * as it's passing in DrugCosts instead of relying on values set in AppState,
 * based on a plan also in AppState.
 *
 * Other types of messages with higher priority and `active` status will take
 * precedence over network-status messages.
 */
export function getNetworkStatusMessageForPlan({
  availableMessages,
  costs,
  hasMailOrderPharmacies,
  t,
  pageContext,
}: {
  availableMessages: AlertMessageType[];
  costs: DrugCosts[];
  hasMailOrderPharmacies: boolean;
  t: UseTranslateType;
  pageContext: PageContext;
}): FormattedAlertMessage | undefined {
  const { planType } = pageContext;
  if (costs.length === 0 || planType === PlanType.MA) {
    return;
  }
  let result = undefined;
  let costsToUse = [...costs];
  if (!hasMailOrderPharmacies) {
    costsToUse = [...costs].filter(cost => !cost.mail_order);
  }
  // Concat all messages for Plan Compare, because genral alert messages set
  // to `active` will likely get priority over network status messages, if they
  // have a higher priority in the hierarchy (as of the time this was written,
  // they all do)
  const messageOrderHierarchy = alertMessageHierarchy[
    AlertMessageHierarchyKeys.PLAN_COMPARISON_NETWORK_STATUS
  ].concat(
    alertMessageHierarchy[AlertMessageHierarchyKeys.PLAN_COMPARISON_TABLE]
  );
  const networkStatuses = costsToUse.reduce(
    (acc, curr) => {
      return {
        in_network: acc.in_network ? acc.in_network : curr.in_network,
        preferred: acc.preferred ? acc.preferred : curr.preferred,
      };
    },
    { in_network: false, preferred: false }
  );
  for (let i = 0; i < messageOrderHierarchy.length; i++) {
    const messageKey = messageOrderHierarchy[i];
    const isNotNetworkStatusMessage =
      messageKey !== AlertMessageConditions.NO_IN_NETWORK_SAVED_PHARMACIES &&
      messageKey !== AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES;
    const hasPriorityOverNetworkMessages = // lower numbers are higher priority
      messageKey < AlertMessageConditions.NO_IN_NETWORK_SAVED_PHARMACIES &&
      messageKey < AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES;
    const selectedMessage = availableMessages[messageKey];
    // If one of the messages unrelated to network status is active, end the loop
    // and return undefined
    if (
      isNotNetworkStatusMessage &&
      hasPriorityOverNetworkMessages &&
      selectedMessage.active
    ) {
      result = undefined;
      break;
    }
    // Manual logic for setting network-status messages based on values taken
    // from drug costs for a specific plan being compared
    if (
      (!networkStatuses.in_network &&
        messageKey === AlertMessageConditions.NO_IN_NETWORK_SAVED_PHARMACIES) ||
      (networkStatuses.in_network &&
        !networkStatuses.preferred &&
        messageKey === AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES)
    ) {
      result = formatMessage({ selectedMessage, t, pageContext });
      continue;
    }
  }
  return result;
}

export function setMessageStatuses({
  messageState,
  appState,
}: {
  messageState: MessagesState;
  appState: AppState;
}): MessagesState {
  const { messages } = messageState;
  const result = messages;
  const beneHasMailOrderPharmacy = hasMailOrderPharmacy(appState);
  const mailOrderCount = beneHasMailOrderPharmacy ? 1 : 0;
  const {
    mailOrderNetworkStatus = { preferred: false, inNetwork: false },
    prescriptions,
    pharmacies,
    beneficiary,
    routeParams,
  } = appState;
  const totalPharmacyCount = pharmacies.length + mailOrderCount;
  const drugsCount = prescriptions.length;
  const isLoggedIn = Boolean(beneficiary);
  const isAccountStale = beneficiary
    ? isOverThirtyDaysSinceLogin(beneficiary)
    : false;
  const hasInNetworkPharmacy =
    pharmacies.some(pharmacy => pharmacy.in_network) ||
    (beneHasMailOrderPharmacy && mailOrderNetworkStatus.inNetwork);
  const hasPreferredPharmacy =
    pharmacies.some(pharmacy => pharmacy.preferred) ||
    (beneHasMailOrderPharmacy && mailOrderNetworkStatus.preferred);

  const prescriptionsLink = isLoggedIn
    ? Routes.summary.managePrescriptions
    : Routes.managePrescriptions;

  // Conditions in which network pharmcacy status alerts should be suppressed
  let suppressPharmacyNetworkStatusMessages = false;
  const hasRoutePlanId = isUrlPlanIdParam(routeParams?.id || "");
  if (beneficiary && !beneficiary.current_coverage[0]) {
    suppressPharmacyNetworkStatusMessages = !hasRoutePlanId;
  }

  for (const [key, value] of Object.entries(messages)) {
    switch (key) {
      case AlertMessageConditions.NO_SAVED_PHARMACIES.toString():
        result[AlertMessageConditions.NO_SAVED_PHARMACIES] = {
          ...value,
          active: totalPharmacyCount < 1,
          // body is overwritten here to provide state context to link
          body: (_t, pageContext = {}) => {
            const hasPlan = !!pageContext.planId;
            const inNetworkPharmacyFinderLink = Routes.inNetworkPharmacy.replace(
              ":id",
              pageContext?.planId || ""
            );
            const summaryPageAddPharmaciesLink = hasPlan
              ? inNetworkPharmacyFinderLink
              : Routes.pharmacy;

            return (
              <FormattedMessage
                id="lcp.messages.no_saved_pharmacies.body"
                values={{
                  addPharmacies: (
                    <AlertMessageLink
                      to={{
                        pathname: pageContext.isFromSummaryPage
                          ? summaryPageAddPharmaciesLink
                          : inNetworkPharmacyFinderLink,
                        state: pageContext?.routerState,
                      }}
                      condition={AlertMessageConditions.NO_SAVED_PHARMACIES}
                      textKey="lcp.messages.no_saved_pharmacies.body.link.add_pharmacies"
                    />
                  ),
                }}
              />
            );
          },
        };
        break;
      case AlertMessageConditions.NO_SAVED_DRUGS.toString():
        result[AlertMessageConditions.NO_SAVED_DRUGS] = {
          ...value,
          active: drugsCount < 1,
          // body is overwritten here to provide state context to link
          body: (t, pageContext = {}) => (
            <FormattedMessage
              id="lcp.messages.no_saved_drugs.body"
              values={{
                addYourPrescriptionDrugs: (
                  <AlertMessageLink
                    condition={AlertMessageConditions.NO_SAVED_DRUGS}
                    to={{
                      pathname: prescriptionsLink,
                      state: pageContext?.routerState,
                    }}
                    textKey="lcp.messages.no_saved_drugs.body.links.add_your_prescription_drugs"
                  />
                ),
              }}
            />
          ),
        };
        break;
      case AlertMessageConditions.ONE_SAVED_PHARMACY_STALE_ACCOUNT.toString():
        result[AlertMessageConditions.ONE_SAVED_PHARMACY_STALE_ACCOUNT] = {
          ...value,
          active: totalPharmacyCount === 1 && isAccountStale,
          body: (t, pageContext = {}) => {
            const hasPlan = !!pageContext.planId;
            const inNetworkPharmacyFinderLink = Routes.inNetworkPharmacy.replace(
              ":id",
              pageContext?.planId || ""
            );
            const summaryPageAddPharmaciesLink = hasPlan
              ? inNetworkPharmacyFinderLink
              : Routes.pharmacy;
            return (
              <FormattedMessage
                id="lcp.messages.one_saved_pharmacy.stale_account.body"
                values={{
                  addPharmacies: (
                    <AlertMessageLink
                      condition={
                        AlertMessageConditions.ONE_SAVED_PHARMACY_STALE_ACCOUNT
                      }
                      to={{
                        pathname: pageContext.isFromSummaryPage
                          ? summaryPageAddPharmaciesLink
                          : inNetworkPharmacyFinderLink,
                        state: pageContext?.routerState,
                      }}
                      textKey="lcp.messages.one_saved_pharmacy.stale_account.body.links.add_pharmacies"
                    />
                  ),
                }}
              />
            );
          },
        };
        break;
      case AlertMessageConditions.ONE_SAVED_PHARMACY.toString():
        result[AlertMessageConditions.ONE_SAVED_PHARMACY] = {
          ...value,
          active: totalPharmacyCount === 1,
          // body is overwritten here to provide state context to link
          body: (t, pageContext = {}) => {
            const hasPlan = !!pageContext.planId;
            const inNetworkPharmacyFinderLink = Routes.inNetworkPharmacy.replace(
              ":id",
              pageContext?.planId || ""
            );
            const summaryPageAddPharmaciesLink = hasPlan
              ? inNetworkPharmacyFinderLink
              : Routes.pharmacy;
            return (
              <FormattedMessage
                id="lcp.messages.one_saved_pharmacy_with_link.body"
                values={{
                  morePharmacies: (
                    <AlertMessageLink
                      condition={AlertMessageConditions.ONE_SAVED_PHARMACY}
                      to={{
                        pathname: pageContext.isFromSummaryPage
                          ? summaryPageAddPharmaciesLink
                          : inNetworkPharmacyFinderLink,
                        state: pageContext?.routerState,
                      }}
                      textKey="lcp.messages.one_saved_pharmacy.body.link.more_pharmacies"
                    />
                  ),
                }}
              />
            );
          },
        };
        break;
      case AlertMessageConditions.STALE_ACCOUNT_PHARMACIES.toString():
        result[AlertMessageConditions.STALE_ACCOUNT_PHARMACIES] = {
          ...value,
          active: isAccountStale,
        };
        break;
      case AlertMessageConditions.STALE_ACCOUNT_DRUGS.toString():
        result[AlertMessageConditions.STALE_ACCOUNT_DRUGS] = {
          ...value,
          active: isAccountStale,
        };
        break;
      case AlertMessageConditions.NO_IN_NETWORK_SAVED_PHARMACIES.toString():
        result[AlertMessageConditions.NO_IN_NETWORK_SAVED_PHARMACIES] = {
          ...value,
          active:
            !hasInNetworkPharmacy && !suppressPharmacyNetworkStatusMessages,
        };
        break;
      case AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES.toString():
        result[AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES] = {
          ...value,
          active:
            !hasPreferredPharmacy && !suppressPharmacyNetworkStatusMessages,
        };
        break;
    }
  }
  return { messages };
}
